import { useSuspenseQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import * as React from 'react';

import type * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import { webConfig } from '@gitbook/env';
import {
    Button,
    Description,
    Dialog,
    DialogBody,
    DialogHeader,
    Heading,
    HeadingContext,
    LoadingIndicator,
    Tab,
    Tabs,
    toast,
    useDialogAction,
    useDialogClose,
} from '@gitbook/spine-react';
import { assert, assertNever } from '@gitbook/util-ts';

import { useNavigation, useSetAnalyticsScreen } from '../../../navigation';
import {
    apiClient,
    apiQuery,
    getSiteSpacesFromStructure,
    useCurrentOrganization,
} from '../../../state';
import { extractCollectionNames } from '../../../utils';
import { GitSyncCard } from '../../Onboarding/cards';
import { applyNewSpaceTemplate, SpaceTemplateCard, TemplatesList } from '../../Templates';
import { SpaceListSelector } from '../SpaceListSelector';
import { BlankTemplateCard } from './BlankTemplateCard';
import { DuplicateCard } from './DuplicateCard';
import { ImportCard } from './ImportCard';
import { PickExistingSpace, usePickExistingSpaceState } from './PickExistingSpace';
import { TranslationCard } from './TranslationCard';
import type {
    SpaceCreationOnCreateSpace,
    SpaceCreationTarget,
    SpaceCreationTargetSite,
    SpaceCreationTargetSiteSection,
    SpaceCreationTargetSiteVariant,
    SpaceCreationTargetSpace,
} from './types';

export interface SpaceCreationDialogPickExisting {
    spaces: api.Space[];
    revisions: ReadonlyMap<string, api.Revision>;
    collectionNameMap: ReadonlyMap<string, string>;
    onSelectSpace: (space: api.Space) => void;
    onSelectPage: (
        space: api.Space,
        page: api.RevisionPageDocument,
        revision: api.Revision
    ) => void;
}

export interface SpaceCreationDialogProps {
    organizationId: string;
    collectionId?: string;
    target?: SpaceCreationTarget | undefined;
    defaultOpenTranslation?: boolean;
    pickExisting?: SpaceCreationDialogPickExisting;
    /** Use the shared site section/variant content-selection layout. */
    siteContentLayout?: boolean;
}

export function SpaceCreationDialog(props: SpaceCreationDialogProps) {
    const { target = { type: 'space' } } = props;

    switch (target.type) {
        case 'space':
            return <SpaceCreationDialogTargetSpace {...props} target={target} />;
        case 'site':
            return <SpaceCreationDialogTargetSite {...props} target={target} />;
        case 'siteSection':
            return <SpaceCreationDialogTargetSection {...props} target={target} />;
        case 'siteVariant':
            return <SpaceCreationDialogTargetVariant {...props} target={target} />;
        default:
            assertNever(target);
    }
}

function SpaceCreationDialogTargetSpace(
    props: Omit<SpaceCreationDialogProps, 'target'> & {
        target: SpaceCreationTargetSpace;
    }
) {
    const { organizationId, collectionId, target, defaultOpenTranslation, pickExisting } = props;
    const [isPending, startDialogAction] = useDialogAction();
    const { navigate } = useNavigation();

    const onCreateSpace: SpaceCreationOnCreateSpace = async (createSpace, finalize) => {
        await startDialogAction(async ({ close }) => {
            await toast.catch(async () => {
                const space = await createSpace({ collectionId });
                if (finalize) {
                    finalize(space);
                } else {
                    navigate({
                        screen: {
                            type: 'space',
                            space: space.id,
                            organization: space.organization,
                            page: '',
                        },
                    });
                }
                await close();
            }, 'Error while creating space');
        });
    };

    return (
        <SpaceCreationDialogContainer isPending={isPending}>
            <DialogHeader>
                <Heading>Create space</Heading>
                <Description>
                    Spaces are where your content lives. Choose where your initial content comes
                    from to get started quickly.
                </Description>
            </DialogHeader>
            <SpaceCreateDialogBody
                organizationId={organizationId}
                target={target}
                onCreateSpace={onCreateSpace}
                isPending={isPending}
                defaultOpenTranslation={defaultOpenTranslation}
                pickExisting={pickExisting}
            />
        </SpaceCreationDialogContainer>
    );
}

function SpaceCreationDialogTargetSite(
    props: Omit<SpaceCreationDialogProps, 'target'> & {
        target: SpaceCreationTargetSite;
    }
) {
    const { organizationId, target, defaultOpenTranslation, pickExisting } = props;
    const [isPending, startDialogAction] = useDialogAction();
    const onCreateSpace: SpaceCreationOnCreateSpace = async (createSpace, finalize) => {
        await startDialogAction(async ({ close }) => {
            await toast.catch(async () => {
                const { data: collection } = await apiClient.collections.createCollection(
                    organizationId,
                    {
                        title: target.site.title,
                    }
                );
                const space = await createSpace({ collectionId: collection.id });
                await apiClient.siteSpaces.addSpaceToSite(
                    organizationId,
                    target.site.id,
                    {
                        draft: true,
                        spaceId: space.id,
                    },
                    { returnPreference: 'minimal' }
                );
                if (finalize) {
                    finalize?.(space);
                } else {
                    toast.success('A space has been successfully linked to your site as content', {
                        action: (
                            <Button
                                href={{
                                    screen: {
                                        type: 'space',
                                        space: space.id,
                                        organization: space.organization,
                                        page: '',
                                    },
                                }}
                                label="Go to space"
                            />
                        ),
                    });
                }
                await close();
            }, 'Error while adding space to site');
        });
    };

    return (
        <SpaceCreationDialogContainer isPending={isPending}>
            <DialogHeader>
                <Heading>Add space</Heading>
                <Description>
                    Spaces are where your content lives. Choose where your initial content comes
                    from to get started quickly.
                </Description>
            </DialogHeader>
            <SpaceCreateDialogBody
                organizationId={organizationId}
                target={target}
                onCreateSpace={onCreateSpace}
                isPending={isPending}
                defaultOpenTranslation={defaultOpenTranslation}
                pickExisting={pickExisting}
            />
        </SpaceCreationDialogContainer>
    );
}

function SpaceCreationDialogTargetSection(
    props: Omit<SpaceCreationDialogProps, 'target'> & {
        target: SpaceCreationTargetSiteSection;
    }
) {
    const { organizationId, target, defaultOpenTranslation, pickExisting, siteContentLayout } =
        props;
    // If collectionId is not provided, we try to find the first space in the site structure
    // and use its parent as the collectionId.
    const collectionId = React.useMemo(() => {
        if (props.collectionId) {
            return props.collectionId;
        }
        const spaces = getSiteSpacesFromStructure(target.siteStructure);
        const firstSpace = spaces[0];
        return firstSpace ? firstSpace.space.parent : undefined;
    }, [target.siteStructure, props.collectionId]);
    const [isPending, startDialogAction] = useDialogAction();

    const onCreateSpace: SpaceCreationOnCreateSpace = (createSpace, _finalize, options) => {
        startDialogAction(async () => {
            await toast.catch(async () => {
                const space = await createSpace({ collectionId });
                target.props.onCreateSpace(space, { pageId: options?.pageId });
            }, 'Error while creating space');
        });
    };

    return (
        <SpaceCreationDialogContainer isPending={isPending}>
            {siteContentLayout ? (
                <SiteContentCreationDialogHeader kind="section" />
            ) : (
                <DialogHeader>
                    <Heading>Add section</Heading>
                    <Description>
                        Each section is linked to a space — where your content lives. Choose where
                        your initial content comes from to get started quickly.
                    </Description>
                </DialogHeader>
            )}
            <SpaceCreateDialogBody
                organizationId={organizationId}
                target={target}
                onCreateSpace={onCreateSpace}
                onSelectBlankSpace={target.props.onSelectBlankSpace}
                isPending={isPending}
                defaultOpenTranslation={defaultOpenTranslation}
                pickExisting={pickExisting}
                siteContentLayout={siteContentLayout}
            />
        </SpaceCreationDialogContainer>
    );
}

function SpaceCreationDialogTargetVariant(
    props: Omit<SpaceCreationDialogProps, 'target'> & {
        target: SpaceCreationTargetSiteVariant;
    }
) {
    const {
        organizationId,
        collectionId,
        target,
        defaultOpenTranslation,
        pickExisting,
        siteContentLayout,
    } = props;
    const [isPending, startDialogAction] = useDialogAction();
    const close = useDialogClose();

    const onCreateSpace: SpaceCreationOnCreateSpace = async (createSpace, finalize, options) => {
        await startDialogAction(async ({ close }) => {
            await toast.catch(async () => {
                const space = await createSpace({ collectionId });
                assert(
                    !finalize,
                    'Finalize callback is not supported when creating a space for a variant'
                );
                target.props.onCreateSpace(space, options);
                if (!siteContentLayout) {
                    await close();
                }
            }, 'Error while creating space');
        });
    };

    useSetAnalyticsScreen(
        analytics.createOrgEvent(organizationId, {
            screen: 'space_create',
        }),
        [organizationId]
    );

    const defaultSectionSpace = target.props.siteSection?.siteSpaces.find(
        (siteSpace) => siteSpace.default
    )?.space;

    return (
        <SpaceCreationDialogContainer isPending={isPending}>
            {siteContentLayout ? (
                <SiteContentCreationDialogHeader kind="variant" />
            ) : (
                <DialogHeader>
                    <Heading>Create variant space</Heading>
                    <Description>
                        Create a new space that will be used as the content for this variant.
                    </Description>
                </DialogHeader>
            )}
            {siteContentLayout ? (
                <SpaceCreateDialogBody
                    organizationId={organizationId}
                    target={target}
                    onCreateSpace={onCreateSpace}
                    onSelectBlankSpace={target.props.onSelectBlankSpace}
                    isPending={isPending}
                    defaultOpenTranslation={defaultOpenTranslation}
                    pickExisting={pickExisting}
                    siteContentLayout
                />
            ) : (
                <DialogBody
                    data-testid="space_create.dialog"
                    className={clsx(
                        'flex flex-col gap-4 [scrollbar-gutter:stable]',
                        isPending && 'overflow-y-hidden'
                    )}
                    scrollable={!isPending}
                >
                    <SpaceCreationBodyContent pickExisting={pickExisting}>
                        <TemplatesSection
                            organizationId={organizationId}
                            onCreateSpace={onCreateSpace}
                            onSelectBlankSpace={
                                target.props.onSelectBlankSpace
                                    ? () => {
                                          target.props.onSelectBlankSpace?.();
                                          close();
                                      }
                                    : undefined
                            }
                        />
                        <SpaceCreationDialogSection>
                            <Heading>Quick actions</Heading>
                            <div className="relative grid grid-cols-3 gap-4">
                                {defaultSectionSpace ? (
                                    <DuplicateCard
                                        space={defaultSectionSpace}
                                        onCreateSpace={onCreateSpace}
                                    />
                                ) : null}
                                <TranslationCard
                                    defaultSpace={defaultSectionSpace}
                                    organizationId={organizationId}
                                    onCreateSpace={onCreateSpace}
                                    defaultOpen={defaultOpenTranslation}
                                />
                            </div>
                        </SpaceCreationDialogSection>
                        <React.Suspense fallback={null}>
                            <PickExistingSpaceSection
                                organizationId={organizationId}
                                target={target}
                                onCreateSpace={onCreateSpace}
                                onlyDuplicate={Boolean(pickExisting)}
                            />
                        </React.Suspense>
                    </SpaceCreationBodyContent>
                </DialogBody>
            )}
        </SpaceCreationDialogContainer>
    );
}

function SiteContentCreationDialogHeader(props: { kind: 'section' | 'variant' }) {
    return (
        <DialogHeader>
            <Heading>Add {props.kind}</Heading>
            <Description>Define the content to use for this {props.kind}.</Description>
        </DialogHeader>
    );
}

function SpaceCreationDialogContainer(props: { isPending: boolean; children: React.ReactNode }) {
    const { isPending, children } = props;
    return (
        <Dialog size={{ width: 880 }} className={isPending ? 'overflow-hidden' : undefined}>
            {isPending && (
                <div className="absolute inset-0 z-20 flex size-full flex-col items-center justify-center gap-2 rounded-md bg-base/70">
                    <LoadingIndicator size="xlarge" />
                    <div className="text-ui-standout">Your space is being created…</div>
                </div>
            )}
            {children}
        </Dialog>
    );
}

function SpaceCreateDialogBody(props: {
    organizationId: string;
    target: SpaceCreationTarget;
    onCreateSpace: SpaceCreationOnCreateSpace;
    onSelectBlankSpace?: () => void;
    isPending: boolean;
    defaultOpenTranslation?: boolean;
    pickExisting?: SpaceCreationDialogPickExisting;
    siteContentLayout?: boolean;
}) {
    const {
        organizationId,
        target,
        onCreateSpace,
        onSelectBlankSpace,
        isPending,
        defaultOpenTranslation,
        pickExisting,
        siteContentLayout,
    } = props;
    useSetAnalyticsScreen(
        analytics.createOrgEvent(organizationId, {
            screen: 'space_create',
        }),
        [organizationId]
    );
    return (
        <DialogBody
            data-testid="space_create.dialog"
            className={clsx(
                'flex flex-col gap-4 [scrollbar-gutter:stable]',
                isPending && 'overflow-y-hidden'
            )}
            scrollable={!isPending}
        >
            {siteContentLayout &&
            (target.type === 'siteSection' || target.type === 'siteVariant') ? (
                <SiteContentCreationTabs
                    organizationId={organizationId}
                    target={target}
                    onCreateSpace={onCreateSpace}
                    onSelectBlankSpace={onSelectBlankSpace}
                    defaultOpenTranslation={defaultOpenTranslation}
                />
            ) : (
                <SpaceCreationBodyContent pickExisting={pickExisting}>
                    <TemplatesSection
                        organizationId={organizationId}
                        onCreateSpace={onCreateSpace}
                        onSelectBlankSpace={onSelectBlankSpace}
                    />
                    <QuickActionsSection
                        organizationId={organizationId}
                        onCreateSpace={onCreateSpace}
                        target={target}
                        defaultOpenTranslation={defaultOpenTranslation}
                    />
                    <React.Suspense fallback={null}>
                        <PickExistingSpaceSection
                            organizationId={organizationId}
                            target={target}
                            onCreateSpace={onCreateSpace}
                            onlyDuplicate={Boolean(pickExisting)}
                        />
                    </React.Suspense>
                </SpaceCreationBodyContent>
            )}
        </DialogBody>
    );
}

function SiteContentCreationTabs(props: {
    organizationId: string;
    target: SpaceCreationTargetSiteSection | SpaceCreationTargetSiteVariant;
    onCreateSpace: SpaceCreationOnCreateSpace;
    onSelectBlankSpace?: () => void;
    defaultOpenTranslation?: boolean;
}) {
    const { organizationId, target, onCreateSpace, onSelectBlankSpace, defaultOpenTranslation } =
        props;
    const organization = useCurrentOrganization();
    const [mode, setMode] = React.useState<'use-existing' | 'create-new'>('create-new');

    return (
        <div className="flex flex-col gap-4">
            <div className="w-fit">
                <Tabs>
                    <Tab
                        active={mode === 'create-new'}
                        label="Create new content"
                        onPress={() => setMode('create-new')}
                    />
                    <Tab
                        active={mode === 'use-existing'}
                        label="Use existing content"
                        onPress={() => setMode('use-existing')}
                    />
                </Tabs>
            </div>
            {mode === 'use-existing' ? (
                <React.Suspense fallback={<LoadingIndicator className="m-auto" size="large" />}>
                    <PickExistingSpaceSection
                        organizationId={organizationId}
                        target={target}
                        onCreateSpace={onCreateSpace}
                        renderEmpty
                        withoutHeading
                        searchPlaceholder="Search content..."
                        bordered
                    />
                </React.Suspense>
            ) : (
                <>
                    <TemplatesSection
                        organizationId={organizationId}
                        onCreateSpace={onCreateSpace}
                        onSelectBlankSpace={onSelectBlankSpace}
                        onSelectTemplate={target.props.onSelectTemplate}
                        disableOpenAPITemplates={
                            !organization.permissions.createContent ||
                            !organization.permissions.createOpenAPISpec
                        }
                        withoutHeading
                    />
                    <QuickActionsSection
                        organizationId={organizationId}
                        onCreateSpace={onCreateSpace}
                        target={target}
                        defaultOpenTranslation={defaultOpenTranslation}
                        translationOnly
                        disableTranslation={!organization.permissions.createContent}
                    />
                </>
            )}
        </div>
    );
}

/**
 * Wraps the dialog's creation content with a "Use existing" / "Create new" switch
 * when a caller supplies `pickExisting`. Without it, renders `children` verbatim.
 */
function SpaceCreationBodyContent(props: {
    pickExisting?: SpaceCreationDialogPickExisting;
    children: React.ReactNode;
}) {
    const { pickExisting, children } = props;
    const close = useDialogClose();
    const [mode, setMode] = React.useState<'use-existing' | 'create-new'>(
        pickExisting && pickExisting.spaces.length > 0 ? 'use-existing' : 'create-new'
    );

    if (!pickExisting) {
        return children;
    }

    return (
        <>
            <div className="w-fit">
                <Tabs>
                    <Tab
                        active={mode === 'use-existing'}
                        label="Use existing"
                        onPress={() => setMode('use-existing')}
                    />
                    <Tab
                        active={mode === 'create-new'}
                        label="Create new"
                        onPress={() => setMode('create-new')}
                    />
                </Tabs>
            </div>
            {mode === 'use-existing' ? (
                <SpaceListSelector
                    spaces={pickExisting.spaces}
                    revisions={pickExisting.revisions}
                    collectionNameMap={pickExisting.collectionNameMap}
                    actionLabel="Use as content"
                    onChange={(space) => {
                        pickExisting.onSelectSpace(space);
                        close();
                    }}
                    onPageChange={(space, page, revision) => {
                        pickExisting.onSelectPage(space, page, revision);
                        close();
                    }}
                />
            ) : (
                children
            )}
        </>
    );
}

function TemplatesSection(props: {
    organizationId: string;
    onCreateSpace: SpaceCreationOnCreateSpace;
    onSelectBlankSpace?: () => void;
    onSelectTemplate?: (template: api.SpaceTemplate) => void;
    disableOpenAPITemplates?: boolean;
    withoutHeading?: boolean;
}) {
    const {
        organizationId,
        onCreateSpace,
        onSelectBlankSpace,
        onSelectTemplate,
        disableOpenAPITemplates,
        withoutHeading,
    } = props;
    return (
        <SpaceCreationDialogSection>
            {withoutHeading ? null : <Heading>Start with a template</Heading>}
            <TemplatesList className={withoutHeading ? 'gap-3' : undefined}>
                <BlankTemplateCard
                    organizationId={organizationId}
                    onCreateSpace={onCreateSpace}
                    onSelectBlankSpace={onSelectBlankSpace}
                    compact={withoutHeading}
                />
                {webConfig.contentTemplates.map((template) => (
                    <SpaceTemplateCard
                        key={template.id}
                        template={template}
                        disabled={Boolean(template.openAPISpec && disableOpenAPITemplates)}
                        disabledReason="You need permission to create both content and OpenAPI specifications."
                        compact={withoutHeading}
                        onApplyTemplate={async (template) => {
                            if (onSelectTemplate && !template.openAPISpec) {
                                onSelectTemplate(template);
                                return;
                            }
                            onCreateSpace(async ({ collectionId }) => {
                                const { space } = await applyNewSpaceTemplate({
                                    template,
                                    organizationId,
                                    collectionId,
                                });
                                return space;
                            });
                        }}
                        organizationId={organizationId}
                    />
                ))}
            </TemplatesList>
        </SpaceCreationDialogSection>
    );
}

function QuickActionsSection(props: {
    organizationId: string;
    onCreateSpace: SpaceCreationOnCreateSpace;
    target: SpaceCreationTarget;
    defaultOpenTranslation?: boolean;
    translationOnly?: boolean;
    disableTranslation?: boolean;
}) {
    const {
        organizationId,
        onCreateSpace,
        target,
        defaultOpenTranslation,
        translationOnly,
        disableTranslation,
    } = props;
    if (translationOnly) {
        const defaultSpace = (() => {
            if (target.type === 'siteVariant') {
                return target.props.siteSection?.siteSpaces.find((siteSpace) => siteSpace.default)
                    ?.space;
            }
            if (target.type === 'siteSection') {
                return getSiteSpacesFromStructure(target.siteStructure)[0]?.space;
            }
            return undefined;
        })();
        return (
            <SpaceCreationDialogSection>
                <Heading>Quick actions</Heading>
                <div className="relative grid grid-cols-3 gap-4">
                    <TranslationCard
                        defaultSpace={defaultSpace}
                        organizationId={organizationId}
                        onCreateSpace={onCreateSpace}
                        defaultOpen={defaultOpenTranslation}
                        disabled={disableTranslation}
                        disabledReason="Only organization creators and admins can create translations."
                    />
                </div>
            </SpaceCreationDialogSection>
        );
    }
    // To create a section, these quick actions don't make sense
    // - GitSync can be setup later, flow is totally async
    // - Translating a section is not a use case, it's at variant level
    // - Import is async, not suited for section creation flow, they can start blank and do it later
    if (target.type === 'siteSection') {
        return null;
    }
    return (
        <SpaceCreationDialogSection>
            <Heading>Quick actions</Heading>
            <div className="relative grid grid-cols-3 gap-4">
                <GitSyncCard organizationId={organizationId} onCreateSpace={onCreateSpace} />
                <TranslationCard
                    organizationId={organizationId}
                    onCreateSpace={onCreateSpace}
                    defaultOpen={defaultOpenTranslation}
                />
                <ImportCard organizationId={organizationId} onCreateSpace={onCreateSpace} />
            </div>
        </SpaceCreationDialogSection>
    );
}

function PickExistingSpaceSection(props: {
    organizationId: string;
    target: SpaceCreationTarget;
    onCreateSpace: SpaceCreationOnCreateSpace;
    /** Only render for the 'duplicate' action — used when a sibling "Use existing" tab already covers 'use'. */
    onlyDuplicate?: boolean;
    renderEmpty?: boolean;
    withoutHeading?: boolean;
    searchPlaceholder?: string;
    bordered?: boolean;
}) {
    const {
        organizationId,
        target,
        onCreateSpace,
        onlyDuplicate,
        renderEmpty,
        withoutHeading,
        searchPlaceholder,
        bordered,
    } = props;
    const { data: orgAllContent } = useSuspenseQuery(
        apiQuery('getOrganizationAllContent', { organizationId })
    );
    const state = usePickExistingSpaceState({ target, orgAllContent });
    if (onlyDuplicate && state.action !== 'duplicate') {
        return null;
    }
    if (!renderEmpty && state.spaces.length === 0) {
        return null;
    }

    const content = (
        <PickExistingSpace
            state={state}
            organizationId={organizationId}
            onCreateSpace={onCreateSpace}
            collectionNameMap={extractCollectionNames(orgAllContent)}
            searchPlaceholder={searchPlaceholder}
            alwaysShowSearch={Boolean(searchPlaceholder)}
            autoFocus={Boolean(searchPlaceholder)}
            bordered={bordered}
        />
    );

    return withoutHeading ? (
        content
    ) : (
        <SpaceCreationDialogSection>
            <Heading>
                {(() => {
                    switch (state.action) {
                        case 'duplicate':
                            return 'Duplicate an existing space';
                        case 'use':
                            return 'Pick existing content';
                        default:
                            assertNever(state.action, `Unknown action: ${state.action}`);
                    }
                })()}
            </Heading>
            {content}
        </SpaceCreationDialogSection>
    );
}

function SpaceCreationDialogSection(props: React.ComponentPropsWithoutRef<'div'>) {
    return (
        <HeadingContext.Provider value={{ level: 3, className: 'text-ui-heading-medium' }}>
            <div {...props} className={clsx('flex flex-col gap-4', props.className)} />
        </HeadingContext.Provider>
    );
}
